import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Users, TrendingUp, Handshake } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { services } from "@/data/services";

const FORM_LOCATIONS = {
  "managed-it-support": "service_managed_it",
  "cloud-cybersecurity": "service_cloud_cybersecurity",
  "network-infrastructure": "service_network",
  "custom-software": "service_custom_software",
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <SEO title={service.seoTitle} description={service.seoDescription} />

      <section className="bg-[#F8FAFC] pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto max-w-3xl">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
            <Link to="/services" className="hover:text-[#0D9488] transition-colors duration-150">Services</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span className="text-slate-700">{service.title}</span>
          </nav>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-[#0B2545]">{service.title}</h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">{service.subtitle}</p>
          <Link to="/contact" data-testid="service-hero-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200 hover:-translate-y-0.5">
            Book a Free Consultation <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#0B2545]">What's included</h2>
            <ul className="mt-6 space-y-4">
              {service.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-slate-700 leading-relaxed">
                  <Check size={18} className="mt-1 shrink-0 text-[#17B8A6]" aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="flex items-center gap-2.5">
                <Users size={20} className="text-[#0D9488]" aria-hidden="true" />
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#0B2545]">Who it's for</h2>
              </div>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">{service.whoFor}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="flex items-center gap-2.5">
                <TrendingUp size={20} className="text-[#0D9488]" aria-hidden="true" />
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#0B2545]">Why it matters</h2>
              </div>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">{service.why}</p>
            </div>
          </div>
        </div>

        {service.partnerNote && (
          <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-10">
            <div data-testid="service-partner-note" className="rounded-xl border border-teal-200 bg-[#CCFBF1]/40 p-5 flex items-start gap-3 max-w-3xl">
              <Handshake size={20} className="mt-0.5 shrink-0 text-[#0D9488]" aria-hidden="true" />
              <p className="text-sm text-slate-700 leading-relaxed">{service.partnerNote}</p>
            </div>
          </div>
        )}
      </section>

      <section className="bg-[#0B2545] py-16 md:py-20">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Talk to us about {service.title.toLowerCase()}</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">A free 30-minute consultation, in plain English, with zero obligation.</p>
          <div data-testid="service-cta-form" className="mt-10 max-w-2xl mx-auto rounded-2xl bg-white p-6 sm:p-8 text-left shadow-2xl">
            <ContactForm formLocation={FORM_LOCATIONS[service.slug] || "service_page"} compact />
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
